/**
 * Shared in-memory fetch cache with in-flight deduplication.
 *
 * Several components (TopBar, dashboardv2, contribution popup) request the
 * same endpoints on mount and on independent polling intervals. Routing them
 * through cachedFetch() collapses simultaneous calls into one request and
 * serves repeat calls from the cache until the TTL expires.
 */

const cache = new Map(); // key -> { value, expiresAt }
const inflight = new Map(); // key -> Promise

/**
 * @param {string} key        Cache key — include the username for user-scoped data.
 * @param {Function} fetcher  Async function producing the value.
 * @param {number} ttlMs      How long a resolved value stays fresh.
 * @param {boolean} force     Bypass the TTL cache (in-flight dedup still applies).
 */
export async function cachedFetch(key, fetcher, ttlMs = 30000, { force = false } = {}) {
  if (!force) {
    const entry = cache.get(key);
    if (entry && Date.now() < entry.expiresAt) return entry.value;
  }
  if (inflight.has(key)) return inflight.get(key);

  const promise = (async () => {
    try {
      const value = await fetcher();
      cache.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

/**
 * Drop cached entries. `keyOrPrefix` matches exact keys and key prefixes,
 * so 'savedSearches' clears 'savedSearches:alice', 'savedSearches:bob', …
 */
export function invalidateFetchCache(keyOrPrefix) {
  for (const key of cache.keys()) {
    if (key === keyOrPrefix || key.startsWith(`${keyOrPrefix}:`)) {
      cache.delete(key);
    }
  }
}
